import { useState } from "react";
import {
  Button,
  Card,
  DataTable,
  PageHead,
  Panel,
  SelectX,
  StatusPill,
  useToast,
  BarRow,
  type Column,
} from "@guestpilot/ui-kit";
import { useGet, useWrite } from "../api/hooks";
import type { DomainClass, ExportJob, Schema } from "../api/types";
import { useHandleApiError } from "../errors";
import { formatDate } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";

type Domain = Schema<"DomainPolicy">;
const CLASSES: DomainClass[] = ["trusted", "known_partner", "unknown", "personal", "watchlist"];

/** Which organisations the guests come from, and the trust class per domain, the single source of the unknown-domain risk (BR-100…BR-104). */
export function Domains() {
  const { t, locale } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const list = useGet<Domain[]>("/api/v1/domains");
  const classify = useWrite<Domain, Schema<"DomainClassify">>("PATCH", "/api/v1/domains/:domain", [
    "/api/v1/domains",
    "/api/v1/participants",
    "/api/v1/dashboard",
    "/api/v1/teams",
  ]);
  const bulk = useWrite<unknown, Schema<"DomainBulkClassify">>("POST", "/api/v1/domains/bulk-classify", [
    "/api/v1/domains",
    "/api/v1/participants",
    "/api/v1/dashboard",
    "/api/v1/teams",
  ]);
  const exportM = useWrite<ExportJob, Schema<"ExportCreate">>("POST", "/api/v1/exports", ["/api/v1/exports"]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<DomainClass>("known_partner");
  const rows = [...(list.data ?? [])].sort((a, b) => (b.usersCount ?? 0) - (a.usersCount ?? 0));
  const max = Math.max(1, ...rows.map((r) => r.usersCount ?? 0));
  const tz = s.me.tenant.timeZone;
  const editGate = s.gate("DOMAIN_EDIT");
  const options = CLASSES.map((c) => ({ value: c, label: t(`ui:domainClass.${c}`) }));

  const columns: Column<Domain>[] = [
    {
      key: "domain",
      header: t("ui:domains.col.domain"),
      render: (d) => <span className="t-body-strong">{d.domain}</span>,
    },
    {
      key: "class",
      header: t("ui:domains.col.class"),
      render: (d) => (
        <SelectX
          aria-label={t("ui:domains.classFor", { domain: d.domain ?? "" })}
          value={d.classification}
          disabled={Boolean(editGate)}
          title={editGate}
          options={options}
          onChange={(e) =>
            classify.mutate(
              {
                params: { domain: d.domain ?? "" },
                body: {
                  classification: e.target.value as DomainClass,
                  ...(d.version !== undefined ? { version: d.version } : {}),
                },
              },
              { onError: fail },
            )
          }
        />
      ),
    },
    { key: "users", header: t("ui:domains.col.users"), numeric: true, render: (d) => d.usersCount ?? 0 },
    {
      key: "teams",
      header: t("ui:domains.col.teams"),
      render: (d) => (d.teams ?? []).map((x) => x.displayName).join(", ") || "—",
    },
    { key: "first", header: t("ui:domains.col.first"), render: (d) => formatDate(d.firstSeenAt, locale, tz) },
  ];

  return (
    <div className="gp-page">
      <PageHead
        title={t("ui:domains.title")}
        subtitle={t("ui:domains.lead")}
        actions={
          <Button
            disabledReason={s.can("EXPORT_CREATE") ? undefined : t("ui:gate.noPermission")}
            onClick={() =>
              exportM.mutate(
                { body: { scope: "domains", format: "csv" } },
                { onSuccess: () => toast.success(t("ui:exports.queued")), onError: fail },
              )
            }
          >
            {t("ui:domains.export")}
          </Button>
        }
      />
      <Card title={t("ui:domains.perDomain")}>
        <div className="gp-stack">
          {rows.slice(0, 12).map((d) => (
            <BarRow
              key={d.domain}
              label={d.domain ?? ""}
              value={d.usersCount ?? 0}
              max={max}
              right={
                d.classification ? (
                  <StatusPill
                    domain="domain"
                    value={d.classification}
                    label={t(`ui:domainClass.${d.classification}`)}
                  />
                ) : undefined
              }
            />
          ))}
          {rows.length === 0 && !list.isLoading ? (
            <p className="t-body gp-muted" style={{ margin: 0 }}>
              {t("ui:domains.empty")}
            </p>
          ) : null}
        </div>
      </Card>
      <section className="gp-stack" aria-labelledby="dc-h">
        <div className="gp-section-head">
          <h2 id="dc-h" className="t-title-sm" style={{ margin: 0 }}>
            {t("ui:domains.classification")}
          </h2>
          {selected.size > 0 ? (
            <div className="gp-row">
              <SelectX
                aria-label={t("ui:domains.bulkTo")}
                value={target}
                options={options}
                onChange={(e) => setTarget(e.target.value as DomainClass)}
              />
              <Button
                disabledReason={editGate}
                onClick={() =>
                  bulk.mutate(
                    { body: { domains: [...selected], classification: target } },
                    { onSuccess: () => setSelected(new Set()), onError: fail },
                  )
                }
              >
                {t("ui:domains.bulkApply", { n: selected.size })}
              </Button>
            </div>
          ) : null}
        </div>
        <Panel label={t("ui:domains.classification")}>
          <DataTable
            caption={t("ui:domains.classification")}
            columns={columns}
            rows={list.data ? rows : undefined}
            loading={list.isLoading}
            error={list.isError}
            onRetry={() => void list.refetch()}
            errorTitle={t("ui:table.error")}
            retryLabel={t("common:retry")}
            rowKey={(d) => d.domain ?? ""}
            emptyTitle={t("ui:domains.empty")}
            selectable={!editGate}
            selected={selected}
            onSelectionChange={setSelected}
            selectAllLabel={t("ui:users.selectAll")}
            selectRowLabel={(d) => t("ui:users.selectRow", { name: d.domain ?? "" })}
          />
        </Panel>
      </section>
    </div>
  );
}
